import API_BASE from './config';

export function getAgents() {
    return fetch(`${API_BASE}/ai/agents`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to fetch agents');
            return res.json();
        });
}

export function getAiRuntimeConfig() {
    return fetch(`${API_BASE}/ai/config`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) {
                return res.json().then((d) => { throw new Error(d.error || 'Failed to fetch AI config'); });
            }
            return res.json();
        });
}

export function updateAiRuntimeConfigHost(host) {
    return fetch(`${API_BASE}/ai/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ host }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to update AI host'); });
        }
        return res.json();
    });
}

export function getAiServerStatus() {
    return fetch(`${API_BASE}/ai/status`, { headers: { 'Accept': 'application/json' } })
        .then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return {
                    online: false,
                    host: data.host || null,
                    model: data.model || null,
                    message: data.message || 'AI server is offline',
                };
            }
            return data;
        })
        .catch((err) => ({
            online: false,
            message: err.message || 'AI status check failed',
        }));
}

export function warmupAiModel() {
    return fetch(`${API_BASE}/ai/warmup`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
    }).then((res) => {
        return res.json().then((data) => {
            if (!res.ok) {
                throw new Error(data.error || 'Model warmup failed');
            }
            return data;
        });
    });
}

export function getAiPredictions(raceId) {
    if (!raceId) return Promise.resolve([]);

    return fetch(`${API_BASE}/ai/predictions?raceId=${encodeURIComponent(raceId)}`, {
        headers: { 'Accept': 'application/json' },
    })
        .then((res) => {
            if (!res.ok) {
                return res.json().then((d) => { throw new Error(d.error || 'Failed to fetch AI predictions'); });
            }
            return res.json();
        })
        .then((data) => data.predictions || []);
}

export function deleteAiPrediction(predictionId) {
    if (!predictionId) return Promise.reject(new Error('predictionId is required'));

    return fetch(`${API_BASE}/ai/predictions/${encodeURIComponent(predictionId)}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' },
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to delete AI prediction'); });
        }
        return res.json();
    });
}

export function generateOneShotPrompt(raceId) {
    if (!raceId) return Promise.reject(new Error('raceId is required'));

    return fetch(`${API_BASE}/ai/prompt/one-shot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ raceId }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to generate one-shot prompt'); });
        }
        return res.json();
    });
}

export function runAgent(agentKey, raceId = null) {
    return fetch(`${API_BASE}/ai/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ agent: agentKey, raceId }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Agent run failed'); });
        }
        return res.json();
    });
}

export function runPipeline(pipelineKey, raceId = null, extraNotes = '', stepTimeoutMs = null) {
    return fetch(`${API_BASE}/ai/run-pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ pipeline: pipelineKey, raceId, extraNotes, stepTimeoutMs }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Pipeline run failed'); });
        }
        return res.json();
    });
}

export function runPipelineAsync(pipelineKey, raceId = null, extraNotes = '', stepTimeoutMs = null) {
    return fetch(`${API_BASE}/ai/run-pipeline-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ pipeline: pipelineKey, raceId, extraNotes, stepTimeoutMs }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to start pipeline run'); });
        }
        return res.json();
    });
}

export function getPipelineJob(jobId) {
    if (!jobId) return Promise.reject(new Error('jobId is required'));

    return fetch(`${API_BASE}/ai/jobs/${encodeURIComponent(jobId)}`, {
        headers: { 'Accept': 'application/json' },
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => {
                const err = new Error(d.error || 'Failed to load pipeline job status');
                err.status = res.status;
                throw err;
            });
        }
        return res.json();
    });
}

export function getPipelineJobs(limit = 25) {
    return fetch(`${API_BASE}/ai/jobs?limit=${encodeURIComponent(limit)}`, {
        headers: { 'Accept': 'application/json' },
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to load AI job history'); });
        }
        return res.json();
    }).then((data) => data.jobs || []);
}

export function deletePipelineJob(jobId) {
    if (!jobId) return Promise.reject(new Error('jobId is required'));

    return fetch(`${API_BASE}/ai/jobs/${encodeURIComponent(jobId)}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' },
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to delete AI job'); });
        }
        return res.json();
    });
}

export function applyAiPrediction(predictionId, raceId = null) {
    return fetch(`${API_BASE}/ai/apply-prediction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ predictionId, raceId }),
    }).then((res) => {
        if (!res.ok) {
            return res.json().then((d) => { throw new Error(d.error || 'Failed to apply AI prediction'); });
        }
        return res.json();
    });
}
